import { cn } from "@/lib/utils";

type MeterProps = {
	value: number;
	/** `null` = unlimited (renders a dotted track). */
	max: number | null;
	className?: string;
	/** Second, lighter segment stacked after `value` (e.g. pack credits after plan credits). */
	extra?: number;
	warn?: boolean;
};

/** Hairline usage bar. Fills with `transform` so updates stay on the compositor. */
export function Meter({ value, max, extra = 0, warn, className }: MeterProps) {
	if (max == null) {
		return (
			<div
				className={cn("h-1.5 rounded-full bg-dot-grid [--grid-size:6px]", className)}
				aria-hidden
			/>
		);
	}
	const ratio = (n: number) => (max <= 0 ? (n > 0 ? 1 : 0) : Math.min(1, Math.max(0, n / max)));
	const main = ratio(value);
	const over = max > 0 && value > max;
	return (
		<div
			className={cn("relative h-1.5 overflow-hidden rounded-full bg-muted", className)}
			aria-hidden
		>
			{extra > 0 && (
				<div
					className="absolute inset-0 origin-left bg-foreground/25 transition-transform duration-150"
					style={{ transform: `scaleX(${ratio(value + extra)})` }}
				/>
			)}
			<div
				className={cn(
					"absolute inset-0 origin-left transition-transform duration-150",
					over || warn ? "bg-destructive" : "bg-foreground",
				)}
				style={{ transform: `scaleX(${main})` }}
			/>
		</div>
	);
}
